import { INTEGRATION_BRAND_DOMAIN } from '@/lib/integrations/integration-brand-domains'
import type { IntegrationDefinition, IntegrationProvider } from './types'

export const DATA_OUT_INTEGRATION_DEFINITIONS = {
  loops: {
    name: 'Loops',
    brandDomain: INTEGRATION_BRAND_DOMAIN.loops,
    description:
      'Export Brew emails as editable draft campaigns in Loops. Supported layouts and contact personalization are converted automatically.',
    shortDescription: 'Draft campaign export',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'Oct 15, 2026',
    dataDirection: 'data_out',
    integrationType: 'api_key',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/export/loops',
    tutorialSteps: [
      {
        label: 'Configure your Loops sending domain',
        description:
          'Loops requires a configured sending domain before its API can create campaign drafts.',
      },
      {
        label: 'Enable Content API access',
        description:
          'Enable Content API access for your Loops team. The connection check verifies this without creating a draft.',
      },
      {
        label: 'Create and copy an API key',
        description:
          'In Loops, open Settings → API and create an API key for this team. Paste it below.',
        input: {
          type: 'password',
          key: 'apiKey',
          placeholder: 'Enter your Loops API key',
        },
      },
      {
        label: 'Export and review a draft',
        description:
          'In Brew, choose Send → Export template → Loops. Review the converted layout, subject and sender in Loops before sending. For unsupported designs, choose Loops ZIP instead.',
      },
    ],
  },
  braze: {
    name: 'Braze',
    brandDomain: INTEGRATION_BRAND_DOMAIN.braze,
    description:
      'Push generated email templates straight into Braze for campaigns and journeys.',
    shortDescription: 'Marketing automation export',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'Dec 31, 2024',
    dataDirection: 'data_out',
    integrationType: 'api_key',
    tutorialVideoUrl: 'https://www.youtube.com/watch?v=OgWdXkbDO7s',
    tutorialVideoThumbnail:
      'https://img.youtube.com/vi/OgWdXkbDO7s/maxresdefault.jpg',
    tutorialSteps: [
      {
        label: 'Copy your REST endpoint',
        description:
          'Find your REST endpoint in Braze > Settings > APIs and Identifiers and paste it below',
        input: {
          type: 'text',
          key: 'instance',
          placeholder: 'rest.iad-01.braze.com',
        },
      },
      {
        label: 'Create a new API key',
        description:
          'In Braze > Settings > API Keys, create a REST API key with both templates.email.list (connection check) and templates.email.create (template export).',
      },
      {
        label: 'Paste your Identifier',
        description: 'Copy your newly created Identifier and paste it below',
        input: {
          type: 'password',
          key: 'apiKey',
          placeholder: 'Enter your Braze API key',
        },
      },
    ],
  },
  brevo: {
    name: 'Brevo',
    brandDomain: INTEGRATION_BRAND_DOMAIN.brevo,
    description:
      'Sync templates and transactional sends with Brevo (formerly Sendinblue).',
    shortDescription: 'Email marketing & automation',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'May 5, 2026',
    dataDirection: 'data_out',
    integrationType: 'api_key',
    tutorialVideoUrl: 'https://www.youtube.com/watch?v=s98yUWzAE_U',
    tutorialVideoThumbnail:
      'https://img.youtube.com/vi/s98yUWzAE_U/maxresdefault.jpg',
    tutorialSteps: [
      {
        label: 'Open API Keys & MCP',
        description:
          'In Brevo, open Settings > SMTP & API > API Keys & MCP. Enterprise users should switch to the target sub-organization first.',
      },
      {
        label: 'Generate a standard API v3 key',
        description:
          "Click 'Generate a new API key' and leave the MCP server option off. Brew cannot use an SMTP key or MCP key. Copy the key immediately because Brevo won't show it again.",
      },
      {
        label: 'Check Authorized IPs if blocked',
        description:
          'If Brevo blocks the connection, open Settings > Security > Authorized IPs. Add the static egress IPs configured for this Brew deployment. Dynamic Vercel IPs change and cannot be reliably allowlisted; contact Brew support if static egress is not configured.',
      },
      {
        label: 'Paste your standard API v3 key',
        description: 'Paste the standard API v3 key below.',
        input: {
          type: 'password',
          key: 'apiKey',
          placeholder: 'Enter your Brevo API key',
        },
      },
    ],
  },
  customerio: {
    name: 'Customer.io',
    brandDomain: INTEGRATION_BRAND_DOMAIN.customerio,
    description:
      'Send transactional emails through your Customer.io workspace.',
    shortDescription: 'Transactional email provider',
    author: 'Brew',
    availability: 'coming_soon',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'Dec 17, 2024',
    dataDirection: 'data_out',
    integrationType: 'api_key',
    tutorialVideoUrl: 'https://www.youtube.com/watch?v=GOPPMLoAQXY',
    tutorialVideoThumbnail:
      'https://img.youtube.com/vi/GOPPMLoAQXY/maxresdefault.jpg',
    tutorialSteps: [
      {
        label: 'Select your datacenter region',
        description:
          'Choose the region where your Customer.io account is hosted',
        input: {
          type: 'select',
          key: 'region',
          options: [
            { value: 'us', label: 'US (United States)' },
            { value: 'eu', label: 'EU (European Union)' },
          ],
        },
      },
      {
        label: 'Go to API credentials',
        description:
          'In Customer.io, navigate to Settings > Account Settings > API Credentials',
      },
      {
        label: 'Paste your App API Key',
        description:
          "Under 'App API Keys', create a new key and paste it below",
        input: {
          type: 'password',
          key: 'apiKey',
          placeholder: 'Enter your Customer.io App API key',
        },
      },
    ],
  },
  hubspot: {
    name: 'HubSpot',
    brandDomain: INTEGRATION_BRAND_DOMAIN.hubspot,
    description:
      'Export emails to HubSpot Marketing Hub as ready-to-send templates for your campaigns and workflows.',
    shortDescription: 'CRM & marketing automation',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'Jan 29, 2026',
    dataDirection: 'data_out',
    integrationType: 'oauth',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/export/hubspot',
    oAuthProvider: 'hubspot',
    tutorialSteps: [
      {
        label: 'Export an email from Brew',
        description:
          "After creating an email in Brew, click 'Export' and select HubSpot as the destination.",
      },
      {
        label: 'Find your email in HubSpot',
        description:
          "Go to Marketing -> Email, click 'Create email', select 'Regular', then choose 'Custom'.",
      },
      {
        label: 'Open the Brew folder',
        description:
          'Your exported emails are saved in the Brew folder. Re-exporting updates the existing email.',
      },
    ],
  },
  klaviyo: {
    name: 'Klaviyo',
    brandDomain: INTEGRATION_BRAND_DOMAIN.klaviyo,
    description:
      'Push templates into Klaviyo flows and campaigns in one click via OAuth.',
    shortDescription: 'Email & SMS marketing',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'Mar 16, 2026',
    dataDirection: 'data_out',
    integrationType: 'oauth',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/export/klaviyo',
    oAuthProvider: 'klaviyo',
    tutorialSteps: [
      {
        label: 'Connect your Klaviyo account',
        description:
          'Authorize Brew to access your Klaviyo account via one-click OAuth.',
      },
    ],
  },
  iterable: {
    name: 'Iterable',
    brandDomain: INTEGRATION_BRAND_DOMAIN.iterable,
    description:
      'Ship Brew templates into Iterable as reusable assets for cross-channel campaigns and journeys.',
    shortDescription: 'Cross-channel marketing',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'Mar 19, 2026',
    dataDirection: 'data_out',
    integrationType: 'api_key',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/export/iterable',
    tutorialSteps: [
      {
        label: 'Select your API region',
        description: 'Choose the region where your Iterable account is hosted',
        input: {
          type: 'select',
          key: 'region',
          options: [
            { value: 'us', label: 'US (United States)' },
            { value: 'eu', label: 'EU (European Union)' },
          ],
        },
      },
      {
        label: 'Go to API Keys',
        description: 'In Iterable, go to Integrations > API Keys',
      },
      {
        label: 'Create a server-side API key',
        description:
          "Click 'Create API Key', name it (e.g. Brew), select 'Server Side' as the type, and copy the key",
      },
      {
        label: 'Paste your API key',
        description: 'Paste your Iterable API key below',
        input: {
          type: 'password',
          key: 'apiKey',
          placeholder: 'Enter your Iterable API key',
        },
      },
    ],
  },
  postmark: {
    name: 'Postmark',
    brandDomain: INTEGRATION_BRAND_DOMAIN.postmark,
    description:
      'Send templates to Postmark for transactional and broadcast email delivery.',
    shortDescription: 'Transactional email delivery',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'Mar 19, 2026',
    dataDirection: 'data_out',
    integrationType: 'api_key',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/export/postmark',
    tutorialSteps: [
      {
        label: "Go to your server's API Tokens",
        description:
          'In Postmark, click Servers, select your server, then go to the API Tokens tab',
      },
      {
        label: 'Copy your Server API token',
        description:
          'Copy the Server API token shown on the page. If you need a new one, you can generate it here.',
      },
      {
        label: 'Paste your token',
        description: 'Paste your Postmark Server API token below',
        input: {
          type: 'password',
          key: 'apiKey',
          placeholder: 'Enter your Postmark Server API token',
        },
      },
    ],
  },
  onesignal: {
    name: 'OneSignal',
    brandDomain: INTEGRATION_BRAND_DOMAIN.onesignal,
    description: 'Push templates into OneSignal app campaigns and automations.',
    shortDescription: 'Push & email messaging',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'Mar 19, 2026',
    dataDirection: 'data_out',
    integrationType: 'api_key',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/export/onesignal',
    tutorialSteps: [
      {
        label: 'Go to Keys & IDs',
        description:
          "In OneSignal, go to your app's dashboard, click Settings, then Keys & IDs",
      },
      {
        label: 'Copy your App ID',
        description: 'Copy the App ID shown at the top and paste it below',
        input: {
          type: 'text',
          key: 'appId',
          placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
        },
      },
      {
        label: 'Copy your API key',
        description:
          "Create an App API Key if you don't have one, copy it (you'll only see it once), and paste it below",
        input: {
          type: 'password',
          key: 'apiKey',
          placeholder: 'Enter your OneSignal API key',
        },
      },
    ],
  },
  mailgun: {
    name: 'Mailgun',
    brandDomain: INTEGRATION_BRAND_DOMAIN.mailgun,
    description:
      'Save templates to Mailgun for transactional and marketing sends across your domains.',
    shortDescription: 'Email delivery infrastructure',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'Mar 19, 2026',
    dataDirection: 'data_out',
    integrationType: 'api_key',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/export/mailgun',
    tutorialSteps: [
      {
        label: 'Select your Mailgun region',
        description:
          'Choose the region where your Mailgun domain is hosted. Brew stores this per connection and uses the matching API host.',
        input: {
          type: 'select',
          key: 'region',
          options: [
            { value: 'us', label: 'United States' },
            { value: 'eu', label: 'Europe' },
          ],
        },
      },
      {
        label: 'Copy your domain',
        description:
          'In Mailgun, go to Sending > Domain Settings and copy your domain name',
        input: {
          type: 'text',
          key: 'domain',
          placeholder: 'mg.yourdomain.com',
        },
      },
      {
        label: 'Create an account or RBAC API key',
        description:
          'In API Security, create an account-level or RBAC API key with permission to read, create, update, and delete templates for this domain. Do not use a domain sending key.',
        input: {
          type: 'password',
          key: 'apiKey',
          placeholder: 'Enter your Mailgun account or RBAC API key',
        },
      },
    ],
  },
  sendgrid: {
    name: 'SendGrid',
    brandDomain: INTEGRATION_BRAND_DOMAIN.sendgrid,
    description:
      'Push templates into Twilio SendGrid as dynamic templates for transactional and marketing sends.',
    shortDescription: 'Email delivery by Twilio',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'Mar 19, 2026',
    dataDirection: 'data_out',
    integrationType: 'api_key',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/export/sendgrid',
    tutorialSteps: [
      {
        label: 'Select your SendGrid region',
        description:
          'Choose the regional API host configured for this SendGrid account. EU accounts must use the EU endpoint.',
        input: {
          type: 'select',
          key: 'region',
          options: [
            { value: 'us', label: 'United States' },
            { value: 'eu', label: 'Europe' },
          ],
        },
      },
      {
        label: 'Go to API Keys',
        description:
          'In SendGrid, go to Settings > API Keys in the left sidebar',
      },
      {
        label: 'Create an API key',
        description:
          "Click 'Create API Key', choose Restricted Access, and grant read/write access to Template Engine templates and versions. Copy the key right away — it's only shown once.",
      },
      {
        label: 'Paste your API key',
        description: 'Paste your SendGrid API key below',
        input: {
          type: 'password',
          key: 'apiKey',
          placeholder: 'Enter your SendGrid API key',
        },
      },
    ],
  },
  mailjet: {
    name: 'Mailjet',
    brandDomain: INTEGRATION_BRAND_DOMAIN.mailjet,
    description:
      'Save HTML templates to Mailjet for transactional and marketing email campaigns.',
    shortDescription: 'Email delivery & templates',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'Mar 16, 2026',
    dataDirection: 'data_out',
    integrationType: 'dual_api_key',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/export/mailjet',
    tutorialSteps: [
      {
        label: 'Navigate to API Key Management',
        description:
          'In Mailjet, go to Account Settings > API Key Management (SMTP & API Settings).',
      },
      {
        label: 'Paste your API Key',
        description:
          'Copy your Mailjet API Key (public key) and paste it below.',
        input: {
          type: 'password',
          key: 'apiKey',
          placeholder: 'Enter your Mailjet API Key',
        },
      },
      {
        label: 'Paste your Secret Key',
        description:
          'Copy your Mailjet Secret Key (private key) and paste it below.',
        input: {
          type: 'password',
          key: 'secretKey',
          placeholder: 'Enter your Mailjet Secret Key',
        },
      },
    ],
  },
  mailchimp: {
    name: 'Mailchimp',
    brandDomain: INTEGRATION_BRAND_DOMAIN.mailchimp,
    description:
      'Save AI-generated emails as Mailchimp templates for your campaigns and automations.',
    shortDescription: 'Email marketing & campaigns',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'Mar 16, 2026',
    dataDirection: 'data_out',
    integrationType: 'oauth',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/export/mailchimp',
    oAuthProvider: 'mailchimp',
    tutorialSteps: [
      {
        label: 'Connect your Mailchimp account',
        description:
          'Authorize Brew to access your Mailchimp account via one-click OAuth.',
      },
    ],
  },
} satisfies Partial<Record<IntegrationProvider, IntegrationDefinition>>
